import React, { useEffect, useState } from 'react';
import { ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { api } from '../../../context/AuthContext';
import StatusPill from '../StatusPill';

const money = (value, compact = false) => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (compact && Math.abs(n) >= 1000) return `€${Math.round(n / 1000).toLocaleString()}K`;
  return `€${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const relativeDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const Panel = ({ title, action, children, testId }) => (
  <section className="bg-white rounded-xl border border-black/[0.06]" data-testid={testId}>
    <div className="flex items-center justify-between px-4 md:px-5 py-3.5 border-b border-black/[0.05]">
      <h3 className="text-sm font-medium">{title}</h3>
      {action}
    </div>
    <div className="px-4 md:px-5 py-4">{children}</div>
  </section>
);

const LinkAction = ({ label, onClick, testId }) => (
  <button
    onClick={onClick}
    className="text-xs font-medium text-primary hover:opacity-70 inline-flex items-center gap-1"
    data-testid={testId}
  >
    {label} <ArrowRight className="h-3 w-3" />
  </button>
);

const Stat = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted">{label}</p>
    <p className="text-lg font-semibold text-slate-900 mt-1 tabular-nums">{value}</p>
  </div>
);

/**
 * Operational overview for a project: what needs attention, funding, economics,
 * instrument, underlying asset and project activity - all from real data.
 */
export const ProjectOverviewSummary = ({ projectId, overview, onNavigateTab, onRefresh }) => {
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/admin/workspace/activity?limit=6&project_id=${projectId}`)
      .then((res) => setActivity(res.data.events || []))
      .catch(() => setActivity([]));
  }, [projectId]);

  if (!overview) return null;

  const { project, checks = [], checks_complete: done, checks_total: total, instrument, asset, current_nav } = overview;
  const open = checks.filter((c) => !c.ok);
  const progress = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="space-y-4 md:space-y-5" data-testid="project-overview-summary">
      {/* Project status */}
      <Panel
        title="Project status"
        testId="project-status-panel"
        action={<span className="text-xs text-foreground-muted tabular-nums">{done}/{total} complete</span>}
      >
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-4">
          <div
            className={`h-full rounded-full ${progress === 100 ? 'bg-emerald-500' : 'bg-primary'}`}
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="space-y-2.5">
          {open.length === 0 ? (
            <p className="text-sm text-foreground-muted flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              Everything required for this project is configured.
            </p>
          ) : (
            open.map((check) => (
              <div key={check.label} className="flex items-center gap-3" data-testid="project-check-open">
                <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{check.label}</p>
                  <p className="text-xs text-foreground-muted truncate">{check.hint}</p>
                </div>
                <LinkAction label="Fix" onClick={() => onNavigateTab(check.action_tab)} />
              </div>
            ))
          )}

          {checks.filter((c) => c.ok).length > 0 && (
            <div className="pt-2 flex flex-wrap gap-x-5 gap-y-1.5">
              {checks.filter((c) => c.ok).map((check) => (
                <span key={check.label} className="inline-flex items-center gap-1.5 text-xs text-foreground-muted">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  {check.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </Panel>

      <div className="grid gap-4 md:gap-5 lg:grid-cols-2">
        {/* Funding */}
        <Panel
          title="Funding"
          testId="project-funding-panel"
          action={<LinkAction label="Manage subscriptions" onClick={() => onNavigateTab('instruments')} />}
        >
          <p className="text-[32px] leading-none font-semibold tracking-tight tabular-nums" data-testid="project-funding-raised">
            {money(project.funding_raised)}
          </p>
          <p className="text-sm text-foreground-muted mt-1.5">
            raised of {money(project.funding_target)} target
          </p>
          <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-blue-600"
              style={{ width: `${Math.min(project.funding_progress || 0, 100)}%` }}
            />
          </div>
          <div className="mt-2.5 flex items-center justify-between text-sm">
            <span className="font-medium text-blue-700 tabular-nums">{project.funding_progress || 0}% funded</span>
            <span className="text-foreground-muted tabular-nums">
              {(project.units_available || 0).toLocaleString()} units remaining
            </span>
          </div>
        </Panel>

        {/* Economics */}
        <Panel title="Project economics" testId="project-economics-panel">
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            <Stat label="Current asset NAV" value={money(current_nav)} />
            <Stat
              label="Projected return"
              value={instrument?.estimated_project_return != null ? `${Number(instrument.estimated_project_return).toFixed(1)}%` : '—'}
            />
            <Stat
              label="Target hold"
              value={instrument?.max_hold_period_months ? `${instrument.max_hold_period_months} months` : '—'}
            />
            <Stat label="Share price" value={instrument ? money(instrument.unit_price) : '—'} />
          </div>
        </Panel>

        {/* Instrument */}
        <Panel
          title="Investment instrument"
          testId="project-instrument-panel"
          action={<LinkAction label="Manage instrument" onClick={() => onNavigateTab('instruments')} testId="manage-instrument-link" />}
        >
          {instrument ? (
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-foreground-muted">{instrument.code}</span>
                <StatusPill status={instrument.status} />
                <StatusPill status={instrument.offering_status} label={`Offering ${instrument.offering_status}`} />
              </div>
              <p className="text-sm font-medium mt-2">{instrument.name}</p>
              <p className="text-sm text-foreground-muted mt-1.5 tabular-nums">
                {money(instrument.unit_price)} / share · {(instrument.total_authorized_units || 0).toLocaleString()} authorized ·{' '}
                {(instrument.units_available || 0).toLocaleString()} available
              </p>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm font-medium">No instrument yet</p>
              <p className="text-xs text-foreground-muted mt-1 mb-3">
                Investors subscribe to an instrument - create one to open this project for investment.
              </p>
              <LinkAction label="Create instrument" onClick={() => onNavigateTab('instruments')} />
            </div>
          )}
        </Panel>

        {/* Underlying asset */}
        <Panel
          title="Underlying asset"
          testId="project-asset-panel"
          action={<LinkAction label="Manage asset" onClick={() => onNavigateTab('assets')} />}
        >
          {asset ? (
            <div>
              <p className="text-sm font-medium">{asset.name}</p>
              <p className="text-xs text-foreground-muted mt-1">
                <span className="font-mono">{asset.code}</span>
                {asset.location ? ` · ${asset.location}` : ''}
                {asset.asset_type ? ` · ${String(asset.asset_type).replace(/_/g, ' ')}` : ''}
              </p>
              <div className="mt-4">
                <Stat label="Current NAV" value={money(current_nav)} />
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm font-medium">No asset linked</p>
              <p className="text-xs text-foreground-muted mt-1 mb-3">Link the property this project invests in.</p>
              <LinkAction label="Add asset" onClick={() => onNavigateTab('assets')} />
            </div>
          )}
        </Panel>
      </div>

      {/* Activity */}
      <Panel title="Recent activity" testId="project-activity-panel" action={onRefresh ? <LinkAction label="Refresh" onClick={onRefresh} /> : null}>
        {activity.length === 0 ? (
          <p className="text-sm text-foreground-muted py-2">
            No activity recorded for this project yet. Subscriptions, NAV updates and edits will appear here.
          </p>
        ) : (
          <div className="divide-y divide-black/[0.05] -my-1">
            {activity.map((event, i) => (
              <div key={`${event.date}-${i}`} className="py-2.5 flex items-baseline gap-3">
                <span className="text-xs text-foreground-muted w-20 flex-shrink-0">{relativeDay(event.date)}</span>
                <span className="text-sm">{event.title}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

export default ProjectOverviewSummary;
